/**
 * The enriched-products endpoints cap `size` at 200 and honour the cap verbatim
 * (`size=100000` comes back as `size: 200`), so the historical "one huge request"
 * silently truncated any run over 200 products — and the client-side pagination
 * footer counted only what arrived. Page via `totalPages` instead.
 */
export const ENRICHED_PRODUCTS_MAX_PAGE_SIZE = 200;

/** The page envelope every enriched-products endpoint shares. */
interface EnrichedProductsPage {
  products: unknown[];
  page: number;
  size: number;
  totalProducts: number;
  totalPages: number;
}

/**
 * Pulls every page of an enriched-products read and returns it as one page-shaped
 * response (`page: 0`, `size` = row count, `totalPages: 1`), so callers that paginate
 * client-side keep working unchanged. Page 0 goes first for `totalPages`; the rest
 * go out in parallel. Rows are not deduplicated here — the server guarantees one
 * row per `externalId`, and the listing adapter dedupes defensively on it.
 */
export async function fetchAllEnrichedProductPages<TPage extends EnrichedProductsPage>(
  fetchPage: (page: number, size: number) => Promise<TPage>,
): Promise<TPage> {
  const first = await fetchPage(0, ENRICHED_PRODUCTS_MAX_PAGE_SIZE);
  const remainingPages = Math.max(0, first.totalPages - 1);
  const rest =
    remainingPages === 0
      ? []
      : await Promise.all(
          Array.from({ length: remainingPages }, (_, index) =>
            fetchPage(index + 1, ENRICHED_PRODUCTS_MAX_PAGE_SIZE),
          ),
        );
  const products = [...first.products, ...rest.flatMap((page) => page.products)];
  return {
    ...first,
    products,
    page: 0,
    size: products.length,
    // The server's count is the deduplicated total; only exceed it if the pages
    // actually delivered more (the query is unsorted, so a row can shift pages).
    totalProducts: Math.max(first.totalProducts, products.length),
    totalPages: 1,
  };
}
